// §13 Step 8 — Assisted by. Then submit the application → straight to the
// logged-in applicant home (/applicant).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Input from '@/components/ui/Input/Input';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { useOnboarding } from '@/context/OnboardingContext';
import { BRANCHES } from '@/api/constants';
import { submitApplication, getApplicationById, getAgent, OnboardingApiError } from '@/api/onboarding';
import { setSession, setApplicationId, getApplicationId } from '@/api/session';
import './onboarding.css';

const CHANNELS = [
  'Branch staff',
  'Relationship Manager',
  'Direct Sales Agent (DSA)',
  'Business Correspondent (BC) agent',
  'Existing customer referral',
  'Corporate salary tie-up',
];
const CODE_CHANNELS = CHANNELS.slice(0, 4);
const BRANCH_CHANNELS = ['Branch staff', 'Relationship Manager'];

type Errors = Record<string, string>;

const AssistedByForm = () => {
  const navigate = useNavigate();
  const { draft, updateAssistedBy, setSubmitted } = useOnboarding();
  const a = draft.assistedBy;
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [agentChecking, setAgentChecking] = useState(false);
  const [agentInvalid, setAgentInvalid] = useState(false);
  const agentLookupRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live agent lookup — GET /onboarding/agents/{code} as the applicant types.
  useEffect(() => {
    const code = a.agentCode.trim();
    if (agentLookupRef.current) clearTimeout(agentLookupRef.current);
    if (code.length < 4) {
      setAgentChecking(false);
      setAgentInvalid(false);
      return;
    }
    agentLookupRef.current = setTimeout(async () => {
      setAgentChecking(true);
      setAgentInvalid(false);
      try {
        const agent = await getAgent(code);
        updateAssistedBy({
          representativeName: agent.representativeName,
          branchCode: agent.branchCode ?? '',
        });
      } catch {
        updateAssistedBy({ representativeName: '' });
        setAgentInvalid(true);
      } finally {
        setAgentChecking(false);
      }
    }, 400);
    return () => {
      if (agentLookupRef.current) clearTimeout(agentLookupRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.agentCode]);

  const startFresh = () => {
    // The current application is wedged server-side — drop the handle so a
    // new application starts from personal details.
    setApplicationId(null);
    navigate('/open-account', { replace: true });
  };

  const set = (patch: Partial<typeof a>) => {
    updateAssistedBy(patch);
    const key = Object.keys(patch)[0];
    setErrors((e) => ({ ...e, [key]: '' }));
  };

  const repName = a.representativeName || undefined;

  const validate = (): boolean => {
    const e: Errors = {};
    if (!a.wasAssisted) e.wasAssisted = 'Please tell us if someone helped you.';
    if (a.wasAssisted === 'YES') {
      if (!a.channel) e.channel = 'Please select who assisted you.';
      if (CODE_CHANNELS.includes(a.channel)) {
        if (!a.agentCode.trim() || !repName) e.agentCode = 'Invalid employee/agent code.';
      }
      if (BRANCH_CHANNELS.includes(a.channel) && !a.branch) {
        e.branch = 'Please select the branch.';
      }
      if (a.channel === 'Existing customer referral') {
        const ok =
          /^[A-Za-z0-9]{4,12}$/.test(a.referral) || /^[6-9]\d{9}$/.test(a.referral);
        if (!ok) e.referral = 'Enter a referral code or a 10-digit mobile number.';
      }
      if (a.channel === 'Corporate salary tie-up' && !a.corporateCode.trim()) {
        e.corporateCode = 'Company name or corporate code is required.';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = async () => {
    if (!validate()) return;
    setLoading(true);
    setSubmitError('');
    try {
      const result = await submitApplication(draft);
      setSubmitted(result);
      // Application is submitted — clear the in-progress handle and start the
      // tracking session, then go straight to the applicant home.
      setApplicationId(null);
      setSession({
        type: 'TRACKING',
        reference: result.reference,
        mobileNumber: draft.personal.mobile.replace(/\D/g, ''),
      });
      navigate('/applicant', { replace: true });
    } catch (err) {
      if (err instanceof OnboardingApiError && err.code === 'ERR_002') {
        // The backend rejected the submit as a data conflict. It may have
        // succeeded anyway — re-check before declaring failure.
        try {
          const id = getApplicationId();
          const app = id ? await getApplicationById(id) : null;
          if (app?.submittedAt) {
            const result = {
              reference: app.applicationReference,
              customerId: app.customerId,
              product: (draft.product ?? 'SAVINGS') as 'SAVINGS' | 'SALARY' | 'CURRENT',
              mobileMasked: app.maskedMobile,
              firstName: (app.fullName || draft.personal.firstAndMiddleNames).split(' ')[0] || '',
              submittedAt: app.submittedAt,
              handover: '',
            };
            setSubmitted(result);
            setApplicationId(null);
            setSession({
              type: 'TRACKING',
              reference: result.reference,
              mobileNumber: draft.personal.mobile.replace(/\D/g, ''),
            });
            navigate('/applicant', { replace: true });
            return;
          }
        } catch {
          // Fall through to the conflict message below.
        }
        setConflict(true);
        setSubmitError(
          'The bank could not submit this application because its details conflict with an existing record. Please start a new application or contact support.'
        );
      } else {
        setSubmitError(
          err instanceof OnboardingApiError ? err.message : 'Could not submit your application. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout step={8} heading="Did someone help you today?" backTo="/open-account/know-you">
      <p>If a bank representative helped you with this application, let us know so we can recognise them.</p>

      <div className="mb-3">
        <div className="nova-ob-label">Were you assisted in filling this application?</div>
        <div className="nova-ob-seg">
          <button type="button" className={a.wasAssisted === 'YES' ? 'selected' : ''} onClick={() => set({ wasAssisted: 'YES' })}>
            Yes, someone helped me
          </button>
          <button type="button" className={a.wasAssisted === 'NO' ? 'selected' : ''} onClick={() => set({ wasAssisted: 'NO', channel: '', agentCode: '', representativeName: '', branchCode: '', branch: '', referral: '', corporateCode: '' })}>
            No, I did it myself
          </button>
        </div>
        {errors.wasAssisted && <div className="invalid-feedback d-block">{errors.wasAssisted}</div>}
      </div>

      {a.wasAssisted === 'YES' && (
        <>
          <div className="mb-3">
            <div className="nova-ob-label">Assisted by</div>
            <select
              className={`form-select ${errors.channel ? 'is-invalid' : ''}`}
              value={a.channel}
              onChange={(e) => set({ channel: e.target.value, agentCode: '', representativeName: '', branchCode: '', branch: '', referral: '', corporateCode: '' })}
            >
              <option value="">Select</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {errors.channel && <div className="invalid-feedback d-block">{errors.channel}</div>}
          </div>

          {CODE_CHANNELS.includes(a.channel) && (
            <>
              <Input
                id="agentCode"
                label="Employee ID / Agent code"
                placeholder="e.g. NB1042"
                value={a.agentCode}
                error={errors.agentCode}
                onChange={(e) => set({ agentCode: e.target.value.toUpperCase(), representativeName: '', branchCode: '' })}
              />
              {agentChecking && <div className="form-text mb-2">Checking the code…</div>}
              {!agentChecking && agentInvalid && !repName && (
                <div className="invalid-feedback d-block mb-2">Invalid employee/agent code.</div>
              )}
              {repName && (
                <div className="nova-ob-card">
                  <div className="nova-ob-label">Representative name</div>
                  <div className="fw-semibold">{repName}</div>
                  <div className="form-text">Please confirm this is the person who helped.</div>
                </div>
              )}
            </>
          )}

          {BRANCH_CHANNELS.includes(a.channel) && (
            <div className="mb-3">
              <div className="nova-ob-label">Branch</div>
              <select
                className={`form-select ${errors.branch ? 'is-invalid' : ''}`}
                value={a.branch}
                onChange={(e) => {
                  const ifsc = e.target.value.split('·')[1]?.trim() ?? '';
                  set({ branch: e.target.value, branchCode: ifsc.slice(-4) });
                }}
              >
                <option value="">Choose a branch</option>
                {BRANCHES.map((b) => (
                  <option key={b.ifsc} value={`${b.label} · ${b.ifsc}`}>
                    {b.label} · {b.ifsc}
                  </option>
                ))}
              </select>
              {errors.branch && <div className="invalid-feedback d-block">{errors.branch}</div>}
            </div>
          )}

          {a.channel === 'Existing customer referral' && (
            <Input
              id="referral"
              label="Referral code or referrer's mobile number"
              value={a.referral}
              error={errors.referral}
              onChange={(e) => set({ referral: e.target.value })}
            />
          )}

          {a.channel === 'Corporate salary tie-up' && (
            <Input
              id="corporateCode"
              label="Company name / corporate code"
              value={a.corporateCode}
              error={errors.corporateCode}
              helperText="Enables the salary account variant."
              onChange={(e) => set({ corporateCode: e.target.value })}
            />
          )}

          {!showPromo ? (
            <button type="button" className="btn btn-link p-0 mb-3" onClick={() => setShowPromo(true)}>
              Have a promo code?
            </button>
          ) : (
            <Input
              id="promoCode"
              label="Promo / campaign code (optional)"
              value={a.promoCode}
              onChange={(e) => set({ promoCode: e.target.value })}
            />
          )}
        </>
      )}

      <div className="nova-ob-info">
        Bank representatives will never ask for your OTP, MPIN, UPI PIN, CVV or password.
      </div>

      <div className="nova-ob-footer">
        <div className="nova-ob-footer-inner">
          {submitError && (
            <div className="alert alert-danger py-2" role="alert">{submitError}</div>
          )}
          {conflict && (
            <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={startFresh}>
              Start a new application
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-lg w-100 fw-semibold nova-btn"
            disabled={loading}
            onClick={handleContinue}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            Continue
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

const AssistedBy = () => (
  <StepGuard requires={(d) => d.knowYouSaved}>
    <AssistedByForm />
  </StepGuard>
);

export default AssistedBy;
